import React, { useState } from 'react';

import type { CompanyProfileAction } from '../models/CompanyProfile';

type AppContextProps =
  | {
      userId?: string;
      setUserId?: (userId?: string) => void;
      advertiserId?: string;
      setAdvertiserId?: (advertiserId: string) => void;
      companyId?: string;
      setCompanyId?: (companyId?: string) => void;
      companyName?: string;
      setCompanyName?: (companyName: string) => void;
      companyProfileAction?: CompanyProfileAction | undefined;
      setCompanyProfileAction?: (companyAction: CompanyProfileAction) => void;
    }
  | undefined;

type AppContextProviderProps = React.PropsWithChildren<AppContextProps>;

const AppContextProviderContext =
  React.createContext<AppContextProps>(undefined);

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [advertiserIdInternal, setAdvertiserIdInternal] = useState<string>();
  const [userIdInternal, setUserIdInternal] = useState<string>();
  const [companyIdInternal, setCompanyIdInternal] = useState<string>();
  const [companyNameInternal, setCompanyNameInternal] = useState<string>();
  const [companyProfileActionInternal, setCompanyProfileActionInternal] =
    useState<CompanyProfileAction | undefined>(undefined);

  const setAdvertiserId = (advertiserId: string) => {
    setAdvertiserIdInternal(advertiserId);
  };
  const setUserId = (userId?: string) => {
    setUserIdInternal(userId);
  };
  const setCompanyId = (companyId?: string) => {
    setCompanyIdInternal(companyId);
  };
  const setCompanyName = (companyName: string) => {
    setCompanyNameInternal(companyName);
  };
  const setCompanyProfileAction = (
    companyProfileAction: CompanyProfileAction,
  ) => {
    setCompanyProfileActionInternal(companyProfileAction);
  };

  return (
    <AppContextProviderContext.Provider
      value={{
        companyId: companyIdInternal,
        setCompanyId,
        advertiserId: advertiserIdInternal,
        setAdvertiserId,
        userId: userIdInternal,
        setUserId,
        companyName: companyNameInternal,
        setCompanyName,
        companyProfileAction: companyProfileActionInternal,
        setCompanyProfileAction,
      }}
    >
      {children}
    </AppContextProviderContext.Provider>
  );
};

export const useAppContext = () => {
  const context = React.useContext(AppContextProviderContext);

  if (context === undefined) {
    throw new Error('useAppContext must be used within a AppContextProvider');
  }

  return {
    companyId: context.companyId ?? '',
    setCompanyId: context.setCompanyId,
    advertiserId: context.advertiserId ?? '',
    setAdvertiserId: context.setAdvertiserId,
    userId: context.userId ?? '',
    setUserId: context.setUserId,
    companyName: context.companyName ?? '',
    setCompanyName: context.setCompanyName,
    companyProfileAction: context.companyProfileAction,
    setCompanyProfileAction: context.setCompanyProfileAction,
  };
};
